import { Card } from '@acceso/design-system';
import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GeneralHeader } from '@/components/HeaderPage/HeaderPage';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { useSegmentStatementsPermissions } from '@/features/broadcast-schedule-program/hooks/useSegmentStatementsPermissions';
import { useDeleteSegmentDeclaration } from '@/features/segments/api/delete-segment-declaration';
import { TSegmentDeclaration } from '@/features/segments/types';
import { convertISOToSeconds } from '@/utils/hh-mm-ss-into-number';

import { ESegmentToolkit } from '../../types';
import { BroadcastScheduleProgramBodySegmentToolkitNavigation } from '../BroadcastScheduleProgramBodySegmentToolkitNavigation';

import { BroadcastScheduleProgramBodySegmentToolkitDeclarationsForm } from './components/BroadcastScheduleProgramBodySegmentToolkitDeclarationsForm';
import { BroadcastScheduleProgramBodySegmentToolkitDeclarationsTable } from './components/BroadcastScheduleProgramBodySegmentToolkitDeclarationsTable';

export default function BroadcastScheduleProgramBodySegmentToolkitDeclarations() {
  const {
    formBox,
    declarationData,
    activeSegment,
    setFormBox,
    setDeclarationData,
    setActiveSegment,
    handleTooltikClick,
    setTextToHighlight,
    setSegmentItems,
    setVisibleBox,
    videoControlRef,
  } = useBroadcastScheduleProgram();
  const { mutateAsync: deleteSegmentDeclaration } = useDeleteSegmentDeclaration();
  const { t } = useTranslation();
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  const handlePreview = (item: TSegmentDeclaration) => {
    if (highlightedId === item.id) {
      setTextToHighlight('');
      setHighlightedId(null);
      return;
    }
    setTextToHighlight(item.text || '');
    setHighlightedId(item.id || null);
    setVisibleBox(null);

    if (activeSegment?.start_datetime && item.start_datetime) {
      const seconds = convertISOToSeconds(
        item.start_datetime,
        new Date(activeSegment.start_datetime)
      );
      videoControlRef.current?.seekToSecond(seconds);
    }
  };

  const {
    canCreate: canCreateStatements,
    canUpdate: canUpdateStatements,
    canDelete: canDeleteStatements,
  } = useSegmentStatementsPermissions();

  const handleEdit = useCallback(
    (item: TSegmentDeclaration) => {
      if (!canUpdateStatements) return;
      setFormBox('declaration');
      setDeclarationData(item);
    },
    [setFormBox, canUpdateStatements]
  );

  const handleCreate = useCallback(() => {
    if (!canCreateStatements) return;
    setFormBox('declaration');
    setDeclarationData(null);
  }, [setFormBox, setDeclarationData, canCreateStatements]);

  const handleDelete = useCallback(
    (item: TSegmentDeclaration) => {
      if (!canDeleteStatements) return;
      deleteSegmentDeclaration(item.id || '').then(() => {
        setActiveSegment(prev => {
          if (prev) {
            return {
              ...prev,
              segment_declarations: prev.segment_declarations?.filter(logo => logo.id !== item.id),
            };
          }
          return prev;
        });
        setSegmentItems(prev => {
          if (!prev) return prev;
          return prev.map(segment => {
            if (segment.id === activeSegment?.id) {
              return {
                ...segment,
                segment_declarations: segment.segment_declarations?.filter(
                  declaration => declaration.id !== item.id
                ),
              };
            }
            return segment;
          });
        });
      });
    },
    [canDeleteStatements]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key.toLocaleLowerCase() === 'i') {
        event.preventDefault();
        handleCreate();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleCreate]);

  return (
    <Card
      classes={{
        rootSection: clsx('w-full flex-auto order-3 lg:order-2'),
        container: 'py-size-4',
      }}
    >
      <div className="flex w-full flex-col gap-2">
        {formBox !== 'declaration' ? (
          <>
            <GeneralHeader
              name={t('Declaraciones')}
              buttonName={canCreateStatements ? t('Crear') : undefined}
              onClick={handleCreate}
              onClose={() => handleTooltikClick(ESegmentToolkit.PERSONAS)}
            />
            <BroadcastScheduleProgramBodySegmentToolkitDeclarationsTable
              items={activeSegment?.segment_declarations || []}
              onPreview={handlePreview}
              onUpdate={canUpdateStatements ? handleEdit : undefined}
              onDelete={canDeleteStatements ? handleDelete : undefined}
            />
          </>
        ) : (
          <div className="flex flex-col gap-2">
            <BroadcastScheduleProgramBodySegmentToolkitDeclarationsForm />
            <BroadcastScheduleProgramBodySegmentToolkitNavigation
              items={activeSegment?.segment_declarations || []}
              activeItem={declarationData}
              onChangeData={setDeclarationData}
            />
          </div>
        )}
      </div>
    </Card>
  );
}
